import type { Placement } from './placement.logic.ts'

/** Floor projection shared by /map and calculator. Editor uses EDITOR_PPM. */
export const EDITOR_PPM = 100 // must match editorStore.PPM
export const VIEW_PPM = 50
export const CELL_M = 0.6 // editor CELL / PPM

export type FloorRack = {
  x?: number | null
  y?: number | null
  width?: number | null
  height?: number | null
  x_m?: number | null
  y_m?: number | null
  width_m?: number | null
  depth_m?: number | null
  rackIndex?: number | null
  rowIndex?: number | null
  rack_index?: number | null
  row_index?: number | null
  /** Degrees, same as RackInspector / RackConfig / Konva. */
  rotation?: number | null
  placement?: Placement
}

export type ScreenRect = { x: number; y: number; w: number; h: number; rotation: number }

export function rackMeters(rack: FloorRack): { x: number; y: number; w: number; h: number } {
  if (rack.placement) {
    return { x: rack.placement.xM, y: rack.placement.yM, w: rack.placement.widthM, h: rack.placement.depthM }
  }
  const x = rack.x_m != null ? rack.x_m : rack.x != null ? rack.x / EDITOR_PPM : (rack.rack_index ?? rack.rackIndex ?? 0) * CELL_M
  const y = rack.y_m != null ? rack.y_m : rack.y != null ? rack.y / EDITOR_PPM : (rack.row_index ?? rack.rowIndex ?? 0) * CELL_M
  return {
    x,
    y,
    w: rack.width_m ?? (rack.width != null ? rack.width / EDITOR_PPM : CELL_M),
    h: rack.depth_m ?? (rack.height != null ? rack.height / EDITOR_PPM : 1.0),
  }
}

export function rackScreenRect(rack: FloorRack, ppm: number): ScreenRect {
  const m = rackMeters(rack)
  return { x: m.x * ppm, y: m.y * ppm, w: m.w * ppm, h: m.h * ppm, rotation: rack.placement?.orientation ?? rack.rotation ?? 0 }
}

/** Axis-aligned bounds of a rect after clockwise yaw (SVG / Konva, Y-down). */
export function rotatedRectBounds(r: ScreenRect): { minX: number; minY: number; maxX: number; maxY: number } {
  const cx = r.x + r.w / 2
  const cy = r.y + r.h / 2
  const rad = (r.rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [dx, dy] of [
    [-r.w / 2, -r.h / 2],
    [r.w / 2, -r.h / 2],
    [r.w / 2, r.h / 2],
    [-r.w / 2, r.h / 2],
  ] as const) {
    const rx = cx + dx * cos - dy * sin
    const ry = cy + dx * sin + dy * cos
    if (rx < minX) minX = rx
    if (ry < minY) minY = ry
    if (rx > maxX) maxX = rx
    if (ry > maxY) maxY = ry
  }
  return { minX, minY, maxX, maxY }
}

export function floorPlanHref(dcCode: string): string {
  return `/${dcCode}/floor-plan`
}

export function editorHref(dcCode: string): string {
  return floorPlanHref(dcCode)
}

export function calculatorHref(dcCode: string, code?: string): string {
  return `/${dcCode}/calculator/${code ?? dcCode}`
}

export type TraySegment = { x1: number; y1: number; x2: number; y2: number }

function asList(bag: unknown): any[] {
  if (!bag) return []
  return Array.isArray(bag) ? bag : Object.values(bag as Record<string, unknown>)
}

export function nodePixels(n: { x: number; y: number }, ppm: number): { x: number; y: number } {
  return { x: n.x * ppm, y: n.y * ppm }
}

export function trayNodeToEditor(n: any, ppm: number) {
  const p = nodePixels(n, ppm)
  return { ...n, x: Math.round(p.x), y: Math.round(p.y), z_m: n.z_m ?? n.z }
}

export function aisleZoneToEditor(z: any, ppm: number) {
  const x_m = z.x_m ?? z.xM ?? z.x_pos
  const y_m = z.y_m ?? z.yM ?? z.y_pos
  const w_m = z.w_m ?? z.width_m ?? z.widthM ?? z.wM
  const h_m = z.h_m ?? z.height_m ?? z.depth_m ?? z.heightM ?? z.depthM ?? z.hM

  return {
    ...z,
    x: x_m != null ? Math.round(Number(x_m) * ppm) : z.x,
    y: y_m != null ? Math.round(Number(y_m) * ppm) : z.y,
    w: w_m != null ? Math.round(Number(w_m) * ppm) : z.w,
    h: h_m != null ? Math.round(Number(h_m) * ppm) : z.h,
  }
}

export interface AisleZoneScreenRect {
  id: string
  x: number
  y: number
  w: number
  h: number
  type: 'cold' | 'hot' | 'indeterminate'
  isContained: boolean
  label: string
}

export function parseAisleZones(rawZones: unknown, ppm: number): AisleZoneScreenRect[] {
  if (!rawZones) return []
  let bag = rawZones
  if (typeof bag === 'string') {
    try {
      bag = JSON.parse(bag)
    } catch {
      return []
    }
  }
  const list = asList(bag)
  const result: AisleZoneScreenRect[] = []

  for (let i = 0; i < list.length; i++) {
    const z = list[i]
    if (!z || typeof z !== 'object') continue

    const id = String(z.id || `zone_${i + 1}`)
    const xVal = z.x_m ?? z.xM ?? z.x_pos
    const yVal = z.y_m ?? z.yM ?? z.y_pos
    const wVal = z.w_m ?? z.width_m ?? z.widthM ?? z.wM
    const hVal = z.h_m ?? z.height_m ?? z.depth_m ?? z.heightM ?? z.depthM ?? z.hM

    const xPxVal = z.x ?? z.x_px ?? z.xPx
    const yPxVal = z.y ?? z.y_px ?? z.yPx
    const wPxVal = z.w ?? z.width ?? z.width_px ?? z.w_px
    const hPxVal = z.h ?? z.height ?? z.height_px ?? z.depth ?? z.h_px

    const x = xVal != null ? Number(xVal) * ppm : (xPxVal != null ? (Number(xPxVal) / EDITOR_PPM) * ppm : 0)
    const y = yVal != null ? Number(yVal) * ppm : (yPxVal != null ? (Number(yPxVal) / EDITOR_PPM) * ppm : 0)
    const w = wVal != null ? Number(wVal) * ppm : (wPxVal != null ? (Number(wPxVal) / EDITOR_PPM) * ppm : 0)
    const h = hVal != null ? Number(hVal) * ppm : (hPxVal != null ? (Number(hPxVal) / EDITOR_PPM) * ppm : 0)

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
      continue
    }

    const rawType = String(z.aisle_type || z.aisleType || z.type || 'cold').toLowerCase()
    const type: 'cold' | 'hot' | 'indeterminate' = rawType.includes('indeterminate') || rawType.includes('error')
      ? 'indeterminate'
      : rawType.includes('hot') ? 'hot' : 'cold'
    const isContained = Boolean(z.is_contained || z.isContained || z.containment || rawType.includes('containment'))

    let label = typeof z.label === 'string' && z.label.trim().length > 0 ? z.label.trim() : ''
    if (!label) {
      const typeStr = type === 'cold' ? 'Холодный коридор' : type === 'hot' ? 'Горячий коридор' : 'Ошибка ориентации коридора'
      label = isContained ? `${typeStr} (изоляция)` : typeStr
    }

    result.push({
      id,
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      w: Math.round(w * 10) / 10,
      h: Math.round(h * 10) / 10,
      type,
      isContained,
      label,
    })
  }

  return result
}

export function traySegments(trays: unknown, ppm: number): TraySegment[] {
  const graph = trays as { nodes?: unknown; edges?: unknown } | null
  const byId = new Map(asList(graph?.nodes).map((n: any) => [n.id, n]))
  const segs: TraySegment[] = []
  for (const e of asList(graph?.edges)) {
    const a = byId.get(e.source ?? e.from)
    const b = byId.get(e.target ?? e.to)
    if (!a || !b) continue
    segs.push({
      x1: a.x * ppm,
      y1: a.y * ppm,
      x2: b.x * ppm,
      y2: b.y * ppm,
    })
  }
  return segs
}

export type TopologyRackPose = { x: number; z: number; w: number; d: number; rotation: number }

export function topologyRackPose(rack: FloorRack): TopologyRackPose {
  const m = rackMeters(rack)
  return { x: m.x, z: m.y, w: m.w, d: m.h, rotation: rack.placement?.orientation ?? rack.rotation ?? 0 }
}

/** THREE yaw (Y-up, Z = map Y): negative so +deg is clockwise on the floor plan. */
export function topologyYawRad(rotationDeg: number): number {
  return -(rotationDeg * Math.PI) / 180
}

export type TopologyTrayEdge = { x1: number; z1: number; x2: number; z2: number; y: number }
export type TopologyTrayNode = { x: number; z: number; y: number }

export function topologyTrayScene(trays: unknown, defaultElev = 0): {
  edges: TopologyTrayEdge[]
  nodes: TopologyTrayNode[]
} {
  const graph = trays as { nodes?: unknown; edges?: unknown } | null
  const nodeList = asList(graph?.nodes)
  const byId = new Map(nodeList.map((n: any) => [n.id, n]))
  const edges: TopologyTrayEdge[] = []
  for (const e of asList(graph?.edges)) {
    const a = byId.get(e.source ?? e.from)
    const b = byId.get(e.target ?? e.to)
    if (!a || !b) continue
    const y = a.z ?? b.z ?? defaultElev
    edges.push({ x1: a.x, z1: a.y, x2: b.x, z2: b.y, y })
  }
  const nodes: TopologyTrayNode[] = nodeList
    .filter((n: any) => n && n.id != null)
    .map((n: any) => ({ x: n.x, z: n.y, y: n.z ?? defaultElev }))
  return { edges, nodes }
}
